<template>
    <div class="form_group form_group_register_company_party">
        <display-message :option="message_option"></display-message>

        <!-- AML × ordering integration: per-class picker. -->
        <div class="form_row" v-show="aml_picker_enabled && aml_clients.length">
            <label :for="register_company_party_name+'_aml_client'" v-html="cache_data_label.aml_client_id" v-show="cache_data.aml_client_id"></label>
            <div class="select_container">
                <select :id="register_company_party_name+'_aml_client'" v-model="cache_data.aml_client_id" @change="fill_form_from_aml($event)">
                    <option value="">— Fill from AML-verified client —</option>
                    <option :value="aml_client_item.id" v-for="aml_client_item in aml_clients">{{ format_aml_client_option(aml_client_item) }}</option>
                </select>
            </div>
            <!-- AML-verified pill — see UpdateTrustParty.vue for the
                 canonical comment. -->
            <div v-if="aml_selected_client" class="aml_party_pill" :class="aml_selected_client.cdd_status === 'Overdue' ? 'aml_party_pill_warn' : 'aml_party_pill_clear'">
                <span class="aml_party_pill_icon">{{ aml_selected_client.cdd_status === 'Overdue' ? '⏳' : '✓' }}</span>
                <span class="aml_party_pill_label">{{ aml_selected_client.cdd_status === 'Overdue' ? 'Periodic review due' : 'AML-verified' }}</span>
                <span class="aml_party_pill_date" v-if="aml_selected_client.last_review_date">· last reviewed {{ aml_selected_client.last_review_date }}</span>
            </div>
        </div>

        <!-- Verified-required role: free-text forbidden. See
             docs/aml-non-subscriber-ordering-brief.md A3. -->
        <div class="form_row aml_required_hint" v-show="aml_must_pick">
            <p>
                This role must be filled from an <strong>AML-verified client</strong>.
                <template v-if="aml_picker_enabled && aml_clients.length">Select one from the dropdown above.</template>
                <template v-else>No eligible verified clients were found — onboard them in the AML platform first, then return to this order.</template>
            </p>
        </div>

        <div class="form_row" v-show="item_list.stored_party&&available_stored_parties.length && !aml_fields_locked">
            <div class="select_container">
                <select :id="register_company_party_name+'_stored_party'" @change="fill_form_from_stored($event)">
                    <option value="0">-- Fill from Stored Contact --</option>
                    <option :value="stored_party_item.id" v-for="stored_party_item in available_stored_parties">{{ stored_party_item.formatted_name }}</option>
                </select>
            </div>
        </div>

        <div class="form_row" v-show="available_cached_parties.length && !aml_fields_locked">
            <div class="select_container">
                <select :id="register_company_party_name+'_cached_party'" @change="fill_form_from_cached($event)">
                    <option value="0">-- Previously used details --</option>
                    <option :value="cached_party_item.id" v-for="cached_party_item in available_cached_parties">{{ cached_party_item.formatted_name }}</option>
                </select>
            </div>
        </div>

        <div class="form_row" v-show="available_types.length > 1">
            <label :for="register_company_party_name+'_type'" v-html="cache_data_label.type" v-show="cache_data.type"></label>
<!--            <div class="form_static_value" v-show="current_register_company_party.id" v-html="cache_data.type"></div>-->
<!--            <div class="select_container" v-show="!current_register_company_party.id">-->
            <div class="select_container">
                <select :id="register_company_party_name+'_type'" :placeholder="cache_data_label.type" v-model="cache_data.type" :disabled="aml_fields_locked">
                    <option value="" disabled v-show="!cache_data.type">-- {{ cache_data_label.type }} --</option>
                    <option :value="available_type" v-for="available_type in available_types">{{ available_type }}</option>
                </select>
            </div>
        </div>

        <div v-if="cache_data.type == 'Individual'">
            <div class="form_row">
                <label :for="register_company_party_name+'_title'" v-html="cache_data_label.title" v-show="cache_data.title"></label>
                <div class="select_container">
                    <select :id="register_company_party_name+'_title'" :placeholder="cache_data_label.title" v-model="cache_data.title" :disabled="aml_fields_locked" :title="aml_source_tooltip">
                        <option value="" disabled v-show="!cache_data.title">-- {{ cache_data_label.title }} --</option>
                        <option :value="available_title" v-for="available_title in available_titles">{{ available_title }}</option>
                    </select>
                </div>
            </div>
            <div class="form_row">
                <label :for="register_company_party_name+'_first_name'" v-html="cache_data_label.first_name" v-show="cache_data.first_name"></label>
                <input type="text" :id="register_company_party_name+'_first_name'" :placeholder="cache_data_label.first_name" v-model="cache_data.first_name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row">
                <label :for="register_company_party_name+'_middle_name'" v-html="cache_data_label.middle_name" v-show="cache_data.middle_name"></label>
                <input type="text" :id="register_company_party_name+'_middle_name'" :placeholder="cache_data_label.middle_name" v-model="cache_data.middle_name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row">
                <label :for="register_company_party_name+'_last_name'" v-html="cache_data_label.last_name" v-show="cache_data.last_name"></label>
                <input type="text" :id="register_company_party_name+'_last_name'" :placeholder="cache_data_label.last_name" v-model="cache_data.last_name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
        </div>
        <div v-else-if="cache_data.type == 'Company'">
            <!-- contactType is 'Company'/'Other Entity'-->
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;">Please ensure you enter the full name of the company. E.g. " XYZ Pty Ltd"</p>
            <div class="form_row">
                <label :for="register_company_party_name+'_name'" v-html="cache_data_label.name" v-show="cache_data.name"></label>
                <input type="text" :id="register_company_party_name+'_name'" :placeholder="cache_data_label.name" v-model="cache_data.name" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row">
                <label :for="register_company_party_name+'_company_number'" v-html="cache_data_label.company_number" v-show="cache_data.company_number"></label>
                <input type="text" :id="register_company_party_name+'_company_number'" :placeholder="cache_data_label.company_number" v-model="cache_data.company_number" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;">And / Or</p>
            <div class="form_row">
                <label :for="register_company_party_name+'_business_number'" v-html="cache_data_label.business_number" v-show="cache_data.business_number"></label>
                <input type="text" :id="register_company_party_name+'_business_number'" :placeholder="cache_data_label.business_number" v-model="cache_data.business_number" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
        </div>
        <div v-else-if="cache_data.type == 'Joint Member'">
            <div class="form_row form_row_controller_container">
                <label :for="register_company_party_name+'_representative'" v-html="cache_data_label.representative" v-show="cache_data.representative"></label>
                <input type="button" class="btn btn-primary" value="Add Member" @click="set_current_chosen('new','representative')" style="font-size: 0.6em;" v-show="!current_chosen.representative">
            </div>
            <div class="form_row form_row_joint_member_candidate_wrapper form_row_list_container" v-show="!current_chosen.representative">
                <div class="form_row_list" v-for="register_company_party_id in cache_data.representative">
                    <div><span class="form_row_list_name" style="font-weight: normal">{{ item_map.register_company_party[register_company_party_id].formatted_name }}</span>&nbsp;<a class="form_row_list_controller" href="javascript:void(0);" @click="set_current_chosen(register_company_party_id,'representative')" title="Edit"><span class="glyphicon glyphicon-pencil"></span></a><a class="form_row_list_controller" href="javascript:void(0);" @click="remove_attached_register_company_party(register_company_party_id,'representative')" title="Remove"><span class="glyphicon glyphicon-trash"></span></a></div>
                </div>
            </div>
            <update-register-company-party v-show="current_chosen.representative" :current_id="current_chosen.representative" @submit="update_attached_register_company_party" :option="{item_list:item_list,item_map:item_map,class:'representative',register_company_party_name:'representative'}"></update-register-company-party>
        </div>
        <div v-else-if="cache_data.type == 'Trust' || cache_data.type == 'Self Managed Superannuation Fund'">
            <!-- contactType is 'Company'/'Other Entity'-->
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;" v-if="cache_data.type == 'Trust'">Please ensure you enter the full name of the trust. E.g. "The XYZ Trust"</p>
            <p style="font-size: 0.8em;padding: 0 1.5em 1em;margin: 0;" v-else>Please ensure you enter the full name of the fund. E.g. "The XYZ Super Fund"</p>
            <div class="form_row">
                <label :for="register_company_party_name+'_name'" v-html="cache_data_label.name" v-show="cache_data.name"></label>
                <input type="text" :id="register_company_party_name+'_name'" :placeholder="cache_data_label.name" v-model="cache_data.name">
            </div>
        </div>
        <div class="form_row" v-if="['Joint Member','Trust','Self Managed Superannuation Fund'].indexOf(cache_data.type) === -1">
            <label :for="register_company_party_name+'_address'" v-html="cache_data_label.address_id" v-show="cache_data.address_id"></label>
            <p class="highlight_text" style="font-style: italic;font-size: 0.75em;" v-show="option.class == 'contact'">For PO Boxes, fill details in "Street Name". Leave "Care Of" and "Street Number" blank</p>
            <div class="select_container" v-show="cache_data.address_id !== 'Add New'">
                <select :id="register_company_party_name+'_address'" v-model="cache_data.address_id" :placeholder="cache_data_label.address_id" :disabled="aml_fields_locked" :title="aml_source_tooltip">
                    <option value="" disabled v-show="!cache_data.address_id">-- {{ cache_data_label.address_id }} --</option>
                    <option value="0" v-show="option.class === 'stored_party'">Empty Address</option>
                    <option :value="cached_address.id" v-for="cached_address in item_list.address">{{ cached_address.full_address }}</option>
                    <option value="Add New" v-if="!aml_fields_locked">Add New</option>
                </select>
            </div>
        </div>
        <update-address :option="{item_list:item_list,item_map:item_map,address_name:register_company_party_name+'_address'}" v-model:new_address="cache_data.address_id" v-show="cache_data.address_id == 'Add New'"></update-address>
        <div v-show="option.class === 'officer'">
            <div class="form_row">
                <label :for="register_company_party_name+'_position'" v-html="cache_data_label.position" v-show="cache_data.position"></label>
                <div class="select_container">
                    <select :id="register_company_party_name+'_position'" :placeholder="cache_data_label.position" v-model="cache_data.position">
                        <option value="" disabled>-- {{ cache_data_label.position }} --</option>
                        <option value="Director">Director</option>
                        <option value="Secretary">Secretary</option>
                        <option value="Director and Secretary">Director and Secretary</option>
                    </select>
                </div>
            </div>
            <div class="form_row" v-show="cache_data.position == 'Director' || cache_data.position == 'Director and Secretary'">
                <label :for="register_company_party_name+'_director_id'" v-html="cache_data_label.director_id" v-show="cache_data.director_id"></label>
                <input type="text" :id="register_company_party_name+'_director_id'" :placeholder="cache_data_label.director_id" v-model="cache_data.director_id" @change="cache_data.director_id = cache_data.director_id.replace(/[^0-9]+/g, '')">
            </div>
            <p v-show="cache_data.position == 'Director' || cache_data.position == 'Director and Secretary'">* A valid Director ID is required for directors to register new businesses. <a href="https://www.abrs.gov.au/director-identification-number" target="_blank" class="highlight_link">Apply here</a> if do not have one already.</p>

            <div class="form_row form_row_date_container">
                <label for="register_company_birthday" v-html="cache_data_label.birthday"></label>
                <input type="date" id="register_company_birthday" :placeholder="cache_data_label.birthday" v-model="cache_data.birthday" :readonly="aml_fields_locked" :title="aml_source_tooltip">
            </div>
            <div class="form_row" v-show="option.class == 'officer'">
                <label :for="register_company_party_name+'_birth_country'" v-html="cache_data_label.birth_country" v-show="cache_data.birth_country"></label>
                <div class="select_container">
                    <select :id="register_company_party_name+'_birth_country'" :placeholder="cache_data_label.birth_country" v-model="cache_data.birth_country">
                        <option value="" disabled>-- {{ cache_data_label.birth_country }} --</option>
                        <option :value="available_country.id" v-for="available_country in $root.item_map.country">{{ available_country.name }}</option>
                    </select>
                </div>
            </div>
            <div class="form_row" v-show="option.class == 'officer' && cache_data.birth_country == 'AU'">
                <label :for="register_company_party_name+'_birth_state'" v-html="cache_data_label.birth_state" v-show="cache_data.birth_state"></label>
                <div class="select_container">
                    <select :id="register_company_party_name+'_birth_state'" :placeholder="cache_data_label.birth_state" v-model="cache_data.birth_state">
                        <option value="" disabled>-- {{ cache_data_label.birth_state }} --</option>
                        <option value="NSW">NSW</option>
                        <option value="VIC">VIC</option>
                        <option value="QLD">QLD</option>
                        <option value="SA">SA</option>
                        <option value="WA">WA</option>
                        <option value="TAS">TAS</option>
                        <option value="NT">NT</option>
                        <option value="ACT">ACT</option>
                    </select>
                </div>
                <!--                <input v-else type="text" :id="'contact_'+contact_id+'_birth_state'" :placeholder="cache_data_label.birth_state" v-model="cache_data.birth_state">-->
            </div>
            <div class="form_row" v-show="option.class == 'officer'">
                <label :for="register_company_party_name+'_birth_place'" v-html="cache_data_label.birth_place" v-show="cache_data.birth_place"></label>
                <input type="text" :id="register_company_party_name+'_birth_place'" :placeholder="cache_data_label.birth_place" v-model="cache_data.birth_place">
            </div>
        </div>
<!--                <update-register-company-party v-if="option.class == 'shareholder'" v-show="current_chosen.beneficial_owner" :current_contact_id="current_chosen.beneficial_owner" @submit="update_beneficial_owner" :option="{item_list:item_list,item_map:item_map,option.class:'beneficial_owner',register_company_party_name:'beneficial_owner'}"></update-register-company-party>-->

        <div v-if="option.class == 'shareholder'">
            <!-- TODO: Beneficial Owner -->
            <div class="form_row form_row_checkbox_container">
                <label style="font-size: 1.5em;font-weight: normal;" for="beneficial_owner_is_shareholder">Is beneficial owner</label>
                <input id="beneficial_owner_is_shareholder" type="checkbox" v-model="flag_beneficial_owner_is_shareholder" @change="set_beneficial_owner">
            </div>
            <div class="form_row form_row_list_container" v-show="!flag_beneficial_owner_is_shareholder">
                <label :for="register_company_party_name+'_beneficial_owner'" v-html="cache_data_label.beneficial_owner"></label>
                <div class="form_row_list" v-show="!current_chosen.beneficial_owner" v-for="register_company_party_id in cache_data.beneficial_owner">
                    <div><span class="form_row_list_name" style="font-weight: normal;">{{ item_map.register_company_party[register_company_party_id].formatted_name }}</span>&nbsp;<a class="form_row_list_controller" href="javascript:void(0);" @click="set_current_chosen(register_company_party_id,'beneficial_owner')" title="Edit"><span class="glyphicon glyphicon-pencil"></span></a><a class="form_row_list_controller" href="javascript:void(0);" @click="(flag_beneficial_owner_is_shareholder=true)&&remove_attached_register_company_party(register_company_party_id,'beneficial_owner')" title="Remove"><span class="glyphicon glyphicon-trash"></span></a></div>
                </div>
                <update-register-company-party v-show="current_chosen.beneficial_owner" :current_id="current_chosen.beneficial_owner" @submit="update_attached_register_company_party" :option="{item_list:item_list,item_map:item_map,class:'beneficial_owner',register_company_party_name:'beneficial_owner'}"></update-register-company-party>
            </div>

            <div class="form_row">
                <label :for="register_company_party_name+'_share_c_code'" v-html="cache_data_label.share_c_code" v-show="cache_data.share_c_code"></label>
                <div class="select_container">
                    <select :id="register_company_party_name+'_share_c_code'" v-model="cache_data.share_c_code" :placeholder="cache_data_label.share_c_code">
                        <option value="" disabled>-- {{ cache_data_label.share_c_code }} --</option>
                        <option :value="share_class.code" v-for="share_class in available_share_class">{{ share_class.code + ', ' + share_class.description }}</option>
                    </select>
                </div>
            </div>

            <div class="form_row">
                <label :for="register_company_party_name+'_share_paid'" v-html="cache_data_label.share_paid" v-show="cache_data.share_paid"></label>
                <p style="display:block;margin:0;padding: 0 0 0.5em 1em;font-size: 0.8em;">Please type the price per share.</p>
                <input type="text" :id="register_company_party_name+'_share_paid'" :placeholder="cache_data_label.share_paid" v-model="cache_data.share_paid" @change="cache_data.share_paid = $root.fix_format(cache_data.share_paid,[{type:'decimal',precision:2}])">
            </div>

            <div class="form_row">
                <label :for="register_company_party_name+'_share_unpaid'" v-html="cache_data_label.share_unpaid" v-show="cache_data.share_unpaid !== ''"></label>
                <p style="display:block;margin:0;padding: 0 0 0.5em 1em;font-size: 0.8em;">Please type the price per share. E.g. "0" if fully paid. </p>
                <input type="text" :id="register_company_party_name+'_share_unpaid'" :placeholder="cache_data_label.share_unpaid" v-model="cache_data.share_unpaid" @change="cache_data.share_unpaid = $root.fix_format(cache_data.share_unpaid,[{type:'decimal',precision:2}])">
            </div>

            <div class="form_row">
                <label :for="register_company_party_name+'_number_of_shares'" v-html="cache_data_label.number_of_shares" v-show="cache_data.number_of_shares"></label>
                <input type="text" :id="register_company_party_name+'_number_of_shares'" :placeholder="cache_data_label.number_of_shares" v-model="cache_data.number_of_shares" @change="cache_data.number_of_shares = $root.fix_format(cache_data.number_of_shares,[{type:'integer'}])">
            </div>
        </div>
        <div v-if="option.class == 'contact'">
            <div class="form_row">
                <label :for="register_company_party_name+'_name'" v-show="cache_data.name">Company Name</label>
                <input type="text" :id="register_company_party_name+'_name'" placeholder="Company Name" v-model="cache_data.name">
            </div>
            <div class="form_row">
                <label :for="register_company_party_name+'_email'" v-html="cache_data_label.email" v-show="cache_data.email"></label>
                <input type="text" :id="register_company_party_name+'_email'" :placeholder="cache_data_label.email" v-model="cache_data.email">
            </div>
            <div class="form_row">
                <label :for="register_company_party_name+'_phone'" v-html="cache_data_label.phone" v-show="cache_data.phone"></label>
                <input type="text" :id="register_company_party_name+'_phone'" :placeholder="cache_data_label.phone" v-model="cache_data.phone">
            </div>
            <div class="form_row form_row_checkbox_container">
                <label :for="register_company_party_name+'_update_address'" v-html="cache_data_label.update_address"></label>
                <input type="checkbox" :id="register_company_party_name+'_update_address'" name="update_address" v-model="cache_data.update_address">
            </div>
        </div>

        <!-- "Store for Future" checkbox hidden entirely for firms with
             an active AML subscription. See UpdateTrustParty.vue for
             rationale. -->
        <div class="form_row form_row_checkbox_container" v-show="!option.aml_subscription_active && cache_data.type !== 'Joint Member' && cache_data.type !== 'Trust' && option.class !== 'stored_party' && option.class !== 'contact'"">
            <label :for="register_company_party_name+'_store_for_future'" v-html="cache_data_label.store_for_future"></label>
            <input type="checkbox" :id="register_company_party_name+'_store_for_future'" name="store_for_future" v-model="cache_data.store_for_future">
        </div>

        <!-- Todo: Add Field for director_name, multiple Input fields, free text -->

        <div class="form_row form_row_controller_container" v-show="cache_data.address_id != 'Add New'">
            <input type="button" class="btn btn-primary" value="Save" @click="save()">
            <input type="button" class="btn btn-secondary-inverse" value="Cancel" @click="cancel()">
        </div>
    </div>
</template>

<script>
    import amlApi from '../../services/amlApi.js';

    export default {
        name: "UpdateRegisterCompanyParty",
        props: ['option','current_id'],
        data() {
            let data_set = {
                base: this.$parent.base,
                map_name: 'register_company_party',
                autocomplete: null,
                available_titles: ['Mr','Miss','Mrs','Ms','Mstr','Dr'],
                // AML × ordering integration: per-class AML-verified
                // client picker. Populated on mount via
                // /aml/clients/picker?order_type=register_company. See
                // docs/aml-ordering-integration-brief.md MVP § "The party
                // pickers" + Phase 2 § "Extend pickers".
                aml_picker_enabled: false,
                // Verified-required role (config). When true for a
                // subscriber, free-text is forbidden — must pick a cleared
                // AmlClient. Set from picker response meta. See
                // docs/aml-non-subscriber-ordering-brief.md Part A3.
                aml_required: false,
                aml_clients: [],
                cache_data_label: {
                    // class: 'Class',             // Define which type of trust party, some may have different hidden fields. e.g. trustee, settlor, unit_holders
                    type: 'Type',               // Individual or Company, individual defined by title, first_name, middle_name and last_name; company defined by name, acn and abn
                    address_id: 'Address',      //
                    register_company_id: 'Register Company',          // None modifiable field, refer to parent Trust Name

                    // Fields below for type = 'Individual'
                    title: 'Title',
                    first_name: 'First Name',
                    middle_name: 'Middle Name',
                    last_name: 'Last Name',
                    position: 'Position',
                    birthday: 'Date of Birth',
                    birth_place: 'Birth Place/City/Locality',
                    birth_state: 'Birth State',
                    birth_country: 'Birth Country',
                    director_id: 'Director ID',
                    email:'Email',                  // Only available for contact, is required if request electronic version of Trust
                    phone:'Phone',                  // Only available for contact

                    // Fields below for type = 'Company'
                    name:'Name',
                    company_number:'A.C.N.',
                    business_number:'A.B.N.',

                    // Fields below for type = 'Joint Members'
                    representative: 'Member',

                    // Fields below for class = 'shareholder'
                    share_c_code: 'Share Class',
                    share_paid: 'Amount Paid',
                    share_unpaid: 'Amount Unpaid',
                    share_currency: 'Currency',
                    number_of_shares: 'Number of Shares',

                    beneficial_owner: 'Beneficial Owner',

                    update_address: 'Save delivery address',
                    store_for_future: 'Store for Future',
                    aml_client_id: 'AML-verified Client',
                },
                current_chosen: {
                    representative: null,
                    beneficial_owner: null,
                },
                flag_beneficial_owner_is_shareholder: true,
                message_option: {
                    message: '',
                    message_type: ''
                },
            };
            let current_object = this;
            data_set.cache_data = current_object.reset_cache_data({init:true});
            Object.keys(current_object.option).forEach(function (option_key) {
                data_set[option_key] = current_object.option[option_key];
            });
            data_set.available_share_class = [];
            Object.keys(current_object.$root.available_share_class).forEach(function (key) {
                data_set.available_share_class.push({'code':key,'description':current_object.$root.available_share_class[key]});
            });
            console.log('allen debug UpdateRegisterCompanyParty Init',data_set.class, current_object.option.class, current_object,data_set);
            return data_set;
        },
        computed: {
            available_stored_parties: function () {
                let current_object = this;
                let result = [];
                console.log('allen debug available_stored_parties',current_object,current_object.item_list.stored_party);
                if (current_object.option.item_list.stored_party) {
                    let available_types = current_object.available_types;
                    current_object.option.item_list.stored_party.forEach(function (stored_party_item) {
                        if (available_types.indexOf(stored_party_item.type) !== -1) {
                            result.push(stored_party_item);
                        }
                    });
                }
                return result;
            },
            available_cached_parties: function () {
                let current_object = this;
                let result = {};
                if (current_object.item_list[current_object.map_name]) {
                    let available_types = current_object.available_types;
                    current_object.item_list[current_object.map_name].forEach(function (party_item) {
                        if (available_types.indexOf(party_item.type) !== -1) {
                            result[party_item.formatted_name] = party_item;
                        }
                    });
                }
                return Object.values(result);
            },
            available_types: function () {
                let current_object = this;
                let result = ['Individual'];

                // if (['shareholder','representative','beneficial_owner'].indexOf(current_object.option.class) !== -1) {
                if (['shareholder','beneficial_owner','representative'].indexOf(current_object.option.class) !== -1) {
                    result.push('Company');
                }
                if (['shareholder'].indexOf(current_object.option.class) !== -1) {
                    result.push('Joint Member');
                }
                if (['beneficial_owner'].indexOf(current_object.option.class) !== -1) {
                    result.push('Trust');
                    result.push('Self Managed Superannuation Fund');
                }

                return result;
            },
            // AML × ordering: identity-verified fields render readonly /
            // disabled when this party was sourced from a cleared
            // AmlClient. Document-specific fields (position, share_*,
            // birth_*, director_id) stay editable. Admin panel users
            // get all fields editable so they can fix typos.
            aml_locked: function () {
                if (this.$root && this.$root.panel === 'admin') {
                    return false;
                }
                return !!(this.cache_data.aml_client_id);
            },
            // Verified-required role awaiting a pick → block free-text +
            // the save. Admins exempt. See UpdateTrustParty.vue for the
            // canonical comment.
            aml_must_pick: function () {
                if (this.$root && this.$root.panel === 'admin') {
                    return false;
                }
                return !!(this.aml_required && !this.cache_data.aml_client_id);
            },
            aml_fields_locked: function () {
                return this.aml_locked || this.aml_must_pick;
            },
            // Hover-tooltip text for locked identity fields. See
            // UpdateTrustParty.vue::aml_source_tooltip for rationale.
            aml_source_tooltip: function () {
                if (! this.cache_data.aml_client_id) return null;
                let src = (this.aml_clients || []).find(c => c.id === this.cache_data.aml_client_id);
                if (! src) return null;
                let bits = ['Source: ' + src.name];
                if (src.last_review_date) bits.push('last reviewed ' + src.last_review_date);
                if (src.cdd_status === 'Overdue') bits.push('periodic review due');
                return bits.join(' · ');
            },

            // The currently-picked AmlClient object for the
            // AML-verified pill rendered next to the picker. Mirrors
            // aml_selected_client in UpdateTrustParty.vue.
            aml_selected_client: function () {
                if (! this.cache_data.aml_client_id) return null;
                return (this.aml_clients || []).find(c => c.id === this.cache_data.aml_client_id) || null;
            },
        },
        methods: {
            reset_cache_data: function(option) {
                if (typeof option === 'undefined') {
                    option = {};
                }

                let current_object = this;

                let cache_data = {
                    class: '',
                    type: 'Individual',
                    address_id: '',
                    register_company_id: '',
                    aml_client_id: '',

                    // Fields below for type = 'Individual'
                    title: '',
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                    position: '',
                    birthday: '',
                    birth_place: '',
                    birth_state: '',
                    birth_country: '',
                    director_id: '',
                    email:'',                  // Only available for contact, is required if request electronic version of Trust
                    phone:'',                  // Only available for contact

                    // Fields below for type = 'Company'
                    name: '',
                    company_number: '',
                    business_number: '',

                    // Fields below for type = 'Joint Members'
                    representative: [],
                    beneficial_owner: [],

                    // Fields below for class = 'shareholder'
                    share_c_code: '',
                    share_paid: '1.00',
                    share_unpaid: '0.00',
                    number_of_shares: '1',
                    beneficial_owner_id: '0',

                    update_address: false,
                    store_for_future: false,
                };
                if (!option.init) {
                    current_object.cache_data = Object.assign(current_object.cache_data,cache_data);
                    cache_data = current_object.cache_data;
                }

                current_object.current_register_company_party = {};
                if (current_object.option) {
                    cache_data.class = current_object.option.class;
                    if (['company_trustee','trustee'].indexOf(current_object.option.class) !== -1) {
                        // if (current_object.option.class === 'trustee') {
                        cache_data.type = 'Company';
                    }
                    // if (['appointor','beneficiary','beneficiary_b'].indexOf(current_object.option.class) !== -1) {
                    //     cache_data.type = 'Individual';
                    // }
                    // if (current_object.available_types.length === 1) {
                    //     cache_data.type = current_object.available_types[0];
                    // }
                    if (current_object.current_id) {
                        if (current_object.option.item_map['register_company_party'][current_object.current_id]) {
                            cache_data = Object.assign(cache_data, current_object.option.item_map['register_company_party'][current_object.current_id]);
                            current_object.current_register_company_party = current_object.option.item_map['register_company_party'][current_object.current_id];
                            // Coerce null/undefined → '' so v-model on
                            // <select>s matches its placeholder option.
                            // Saved DB rows can carry NULLs that wouldn't
                            // otherwise match anything → empty dropdown.
                            Object.keys(cache_data).forEach(function (key) {
                                if (cache_data[key] === null || typeof cache_data[key] === 'undefined') {
                                    cache_data[key] = '';
                                }
                            });
                        }
                    }
                }

                current_object.flag_beneficial_owner_is_shareholder = !cache_data.beneficial_owner.length;

                return cache_data;

            },

            fill_form_from_cached: function(event) {
                // console.log('fill_form_from_stored',event);
                let current_object = this;
                if (current_object.item_map[current_object.map_name] && current_object.item_map[current_object.map_name][event.target.value]) {
                    let cached_party = current_object.item_map[current_object.map_name][event.target.value];
                    Object.keys(current_object.cache_data).forEach(function (field_name) {
                        if (['type','title','first_name','middle_name','last_name','name','company_number','business_number','director_name','address_id'].indexOf(field_name) !== -1) {
                            current_object.cache_data[field_name] = cached_party[field_name];
                        }
                    });
                }
                event.target.value = 0;
            },

            // AML × ordering: handler for the "Fill from AML-verified
            // client" dropdown. Sets identity fields and
            // cache_data.aml_client_id which the template uses to lock
            // them. Birthday flows through for officer/individual roles.
            fill_form_from_aml: function (event) {
                let current_object = this;
                let picked_id = event && event.target ? event.target.value : '';

                if (!picked_id || picked_id === '0') {
                    current_object.cache_data.aml_client_id = '';
                    return;
                }

                let picked = current_object.aml_clients.find(function (c) { return String(c.id) === String(picked_id); });
                if (!picked) return;

                current_object.cache_data.aml_client_id = picked.id;
                current_object.cache_data.type = picked.client_type;

                if (picked.client_type === 'Individual') {
                    current_object.cache_data.first_name        = picked.first_name  || '';
                    current_object.cache_data.middle_name       = picked.middle_name || '';
                    current_object.cache_data.last_name         = picked.last_name   || '';
                    current_object.cache_data.birthday          = picked.dob          || '';
                    current_object.cache_data.name              = '';
                    current_object.cache_data.company_number    = '';
                    current_object.cache_data.business_number   = '';
                } else if (picked.client_type === 'Company') {
                    current_object.cache_data.name              = picked.name || '';
                    current_object.cache_data.company_number    = picked.acn  || '';
                    current_object.cache_data.business_number   = picked.abn  || '';
                    current_object.cache_data.first_name        = '';
                    current_object.cache_data.middle_name       = '';
                    current_object.cache_data.last_name         = '';
                    current_object.cache_data.birthday          = '';
                }

                if (picked.email) current_object.cache_data.email = picked.email;
                if (picked.phone) current_object.cache_data.phone = picked.phone;

                if (picked.address_id) {
                    current_object.cache_data.address_id = picked.address_id;
                    if (picked.address && !current_object.item_map.address[picked.address_id]) {
                        current_object.item_map.address[picked.address_id] = picked.address;
                        current_object.item_list.address.push(picked.address);
                    }
                }
            },

            format_aml_client_option: function (opt) {
                let parts = [opt.client_type];
                if (opt.cdd_status === 'Overdue') parts.push('review due');
                if (opt.last_review_date) parts.push('last reviewed ' + opt.last_review_date);
                return opt.name + ' (' + parts.join(' · ') + ')';
            },

            fill_form_from_stored: function(event) {
                console.log('fill_form_from_stored',event);
                let current_object = this;
                if (current_object.$root.item_map.stored_party && current_object.$root.item_map.stored_party[event.target.value]) {
                    let stored_party = current_object.$root.item_map.stored_party[event.target.value];
                    Object.keys(current_object.cache_data).forEach(function (field_name) {
                        if (['id','class','description'].indexOf(field_name) !== -1) {
                            return false;
                        }
                        if (typeof stored_party[field_name] !== 'undefined') {
                            current_object.cache_data[field_name] = stored_party[field_name];
                        }
                    });
                }
                if (!current_object.cache_data['address_id']) {
                    current_object.cache_data['address_id'] = '';
                } else {
                    if (!current_object.item_map.address[current_object.cache_data['address_id']] && current_object.$root.item_map.address[current_object.cache_data['address_id']]) {
                        current_object.item_map.address[current_object.cache_data['address_id']] = current_object.$root.item_map.address[current_object.cache_data['address_id']];
                        current_object.item_list.address.push(current_object.item_map.address[current_object.cache_data['address_id']]);
                    }
                }
                event.target.value = 0;
            },

            set_current_chosen: function(register_company_party_id, register_company_party_class) {
                let current_object = this;
                let register_company_party = current_object.base.set_current_chosen(register_company_party_id, register_company_party_class, current_object);

            },

            remove_attached_register_company_party: function(register_company_party_id, register_company_party_class) {
                let current_object = this;
                let register_company_party = current_object.base.remove_attached_register_company_party(register_company_party_id, register_company_party_class, current_object);

            },

            update_attached_register_company_party: function(register_company_party_id, register_company_party_class) {
                let current_object = this;
                let register_company_party = current_object.base.update_attached_register_company_party(register_company_party_id, register_company_party_class, current_object);
                if (register_company_party) {
                    register_company_party.parent_id = current_object.current_id;
                    if (current_object.cache_data[register_company_party_class].indexOf(register_company_party.id) === '-1') {
                        current_object.cache_data[register_company_party_class].push(register_company_party.id);
                    }
                }
                return register_company_party;
            },
            set_beneficial_owner: function() {
                let current_object = this;
                let beneficial_owner_id = current_object.cache_data.beneficial_owner.pop();
                let beneficial_owner;
                if (beneficial_owner_id) {
                    beneficial_owner = current_object.base.item_map['register_company_party'][beneficial_owner_id];
                }
                console.log('set_beneficial_owner',current_object,beneficial_owner_id,beneficial_owner,current_object.flag_beneficial_owner_is_shareholder);
                current_object.cache_data.beneficial_owner.splice(0,current_object.cache_data.beneficial_owner.length);
                if (current_object.flag_beneficial_owner_is_shareholder) {
                    current_object.current_chosen['beneficial_owner'] = '';
                    if (beneficial_owner) {
                        beneficial_owner.deleted = true;
                    }
                } else {
                    if (beneficial_owner) {
                        current_object.set_current_chosen(beneficial_owner_id,'beneficial_owner');
                    } else {
                        current_object.set_current_chosen('new','beneficial_owner');
                    }
                }
            },
            remove_list_item: function (item, list_name) {
                let current_object = this;
                current_object.base.remove_list_item(item, list_name);

            },

            remove_from_list: function (item, list) {
                let index = list.indexOf(item);
                if (index > -1) {
                    list.splice(index,1);
                }
            },

            save: function () {
                // TODO: if store_for_future checked, post stored data to server

                let current_object = this;
                let warning_message = [];
                let mandatory_fields = [];
                let formatted_name = '';
                let post_data = Object.assign({},current_object.cache_data);

                current_object.message_option.message = '';

                // Verified-only ordering (from 1 July 2026): a
                // verified-required role must be filled from an AML-verified
                // client — block the save until one is picked.
                if (current_object.aml_must_pick) {
                    current_object.message_option.message = ['This role must be filled from an AML-verified client — select one from the dropdown above. If they are not listed, onboard them in the AML platform first.'];
                    current_object.message_option.message_type = 'error';
                    return false;
                }

                switch (post_data.type) {
                    case 'Individual':
                        formatted_name = post_data.last_name;
                        if (post_data.middle_name) {
                            formatted_name = post_data.middle_name + ' ' + formatted_name;
                        }
                        if (post_data.first_name) {
                            formatted_name = post_data.first_name + ' ' + formatted_name;
                        }
                        if (post_data.title) {
                            formatted_name = post_data.title + ' ' + formatted_name;
                        }

                        post_data.formatted_name = formatted_name;

                        if (post_data.birth_country !== 'AU') {
                            post_data.birth_state = 'Foreign';
                        }
                        mandatory_fields.push('first_name');
                        mandatory_fields.push('last_name');
                        mandatory_fields.push('address_id');
                        break;
                    case 'Joint Member':
                        let i = 0;
                        let name_stack = [];
                        post_data.formatted_name = '';
                        while (post_data.representative[i]) {
                            if (i > 0) {
                                if (i == post_data.representative.length -1) {
                                    post_data.formatted_name += ' and ';
                                } else {
                                    post_data.formatted_name += ', ';
                                }
                            } else {
                                post_data.address_id = current_object.option.item_map.register_company_party[post_data.representative[i]].address_id;
                            }
                            post_data.formatted_name += current_object.option.item_map.register_company_party[post_data.representative[i]].formatted_name;
                            i++;
                        }
                        post_data.formatted_name += ' as Joint Members';
                        if (i < 2) {
                            warning_message.push('Joint Members should have at least 2 members.');
                        }
                        break;
                    case 'Self Managed Superannuation Fund':
                    case 'Trust':
                        post_data.formatted_name = post_data.name;
                        mandatory_fields.push('name');
                        break;
                    case 'Company':
                    default:
                        post_data.formatted_name = post_data.name;
                        if (post_data.business_number) {
                            post_data.formatted_name += ' (A.B.N. '+post_data.business_number + ')';
                        } else {
                            if (post_data.company_number) {
                                post_data.formatted_name += ' (A.C.N. '+post_data.company_number + ')';
                            } else {
                                warning_message.push('Company must provide A.B.N. and/or A.C.N. for identification.');
                            }
                        }
                        mandatory_fields.push('name');
                        mandatory_fields.push('address_id');

                        break;
                }

                switch (post_data.class) {
                    case 'officer':
                        mandatory_fields.push('position');
                        mandatory_fields.push('birthday');
                        // mandatory_fields.push('birth_place');
                        mandatory_fields.push('birth_country');
                        if (post_data.position == 'Director' || post_data.position == 'Director and Secretary') {
                            mandatory_fields.push('director_id');
                        }
                        break;
                    case 'shareholder':
                        mandatory_fields.push('share_c_code');
                        mandatory_fields.push('number_of_shares');
                        break;
                    case 'contact':
                        mandatory_fields.push('email');
                        mandatory_fields.push('phone');
                        break;

                }
                // General Validation, e.g. mandatory field empty, number format wrong...
                mandatory_fields.forEach(function (field_name) {
                    if (!post_data[field_name]) {
                        warning_message.push('Field "'+current_object.cache_data_label[field_name]+'" is required.');
                    }
                });

                if (post_data.director_id) {
                    // Director ID — 15 digits, "036" prefix is the Australian
                    // country code, last digit is a check digit. ABRS has not
                    // publicly documented the check-digit algorithm and
                    // real-world IDs don't validate under Verhoeff / Luhn /
                    // ISO 7064 Mod-11-2 / Mod 97-10, so we only enforce
                    // format, not a checksum, or we'd reject legitimate IDs.
                    //
                    // Accept the formats users copy off ABRS letters and
                    // various web sources:
                    //   036912345678987
                    //   036 912 345 678 987
                    //   036-912-345-678-987
                    //   DIR036912345678987
                    // …plus invisible characters (zero-width space, soft
                    // hyphen, word joiner, BOM) that clipboard copy-paste
                    // tends to drag in. \s alone misses those, hence the
                    // explicit class.
                    let stripped = String(post_data.director_id)
                        .replace(/^DIR/i, '')                              // optional prefix
                        .replace(/[\s\-\.\|]+/g, '')                       // separators
                        .replace(/[​-‍⁠­﻿]/g, ''); // invisibles \s misses

                    if (stripped.length !== 15) {
                        warning_message.push('Director ID must be 15 digits (got ' + stripped.length + ').');
                    } else if (!/^036\d{12}$/.test(stripped)) {
                        warning_message.push('Director ID must start with "036" (Australian prefix). Allowed input formats: "036912345678987", "036 912 345 678 987", "036-912-345-678-987", or "DIR036912345678987".');
                    } else {
                        // Persist the canonical digits-only form so
                        // downstream consumers (DB, reports, BGL
                        // submissions) get a consistent value regardless
                        // of how the user typed/pasted it.
                        post_data.director_id = stripped;
                    }
                }



                if (warning_message.length > 0) {
                    current_object.message_option.message_type = 'error';
                    current_object.message_option.message = warning_message;
                    return false;
                } else {
                    current_object.message_option.message_type = 'success';
                    current_object.message_option.message = '';
                }

                // current_object.current_register_company_party.class = current_object.register_company_party_class;
                if (!current_object.current_register_company_party.id || current_object.current_register_company_party.id === 'new') {
                    current_object.current_register_company_party = Object.assign(current_object.current_register_company_party,post_data);
                    let index_counter = 1;
                    while (current_object.option.item_map['register_company_party']['new_'+index_counter]) {
                        index_counter++;
                    }
                    current_object.current_register_company_party.id = 'new_'+index_counter;

                    current_object.option.item_map['register_company_party'][current_object.current_register_company_party.id] = current_object.current_register_company_party;
                    current_object.item_list['register_company_party'].push(current_object.current_register_company_party);
                } else {
                    current_object.current_register_company_party = Object.assign(current_object.current_register_company_party,post_data);
                }
                $('html, body').stop().animate({
                    scrollTop:$(current_object.$el).offset().top - 200
                }, 550);
                console.log('allen debug update contact submit',current_object.current_register_company_party.id,current_object.cache_data.class,current_object.option.item_map,current_object.item_list);
                // this.$emit('update:new_contact',this.cache_data.id);
                if (current_object.current_register_company_party.deleted) {
                    delete current_object.current_register_company_party.deleted;
                }
                current_object.$emit('submit',current_object.current_register_company_party.id,current_object.current_register_company_party.class);
            },
            cancel: function () {
                let current_object = this;

                // Erase error message if applicable
                current_object.message_option.message_type = '';
                current_object.message_option.message = '';

                console.log('allen debug update_register_company_party cancel',current_object.current_register_company_party.id,current_object.register_company_party_class,current_object.option.item_map,current_object.item_list);
                // this.$emit('update:new_contact','');
                $('html, body').stop().animate({
                    scrollTop:$(current_object.$el).offset().top - 200
                }, 550);
                // current_object.$emit('submit',current_object.current_register_company_party.id?current_object.current_register_company_party.id:'',current_object.register_company_party_class);
                if (current_object.current_id.match(/^new_/) && current_object.option.item_map['register_company_party'][current_object.current_id].deleted) {
                    delete current_object.option.item_map['register_company_party'][current_object.current_id];
                }
                current_object.$emit('submit','',current_object.cache_data.class);
            }
        },
        mounted() {
            let current_object = this;

            // AML × ordering: fetch cleared AML clients eligible for
            // this party class. 422 from the picker endpoint = class
            // not AML-enabled in config (e.g. contact) → picker stays
            // hidden.
            let party_class = current_object.option && current_object.option.class;
            if (!party_class) return;
            let order_type = (current_object.option && current_object.option.order_type) || 'register_company';

            amlApi.get('clients/picker', {
                order_type:  order_type,
                party_class: party_class,
            }).then(function (response) {
                current_object.aml_clients = response.data || [];
                current_object.aml_required = !!(response.meta && response.meta.required);
                current_object.aml_picker_enabled = true;
            }).catch(function () {
                current_object.aml_clients = [];
                current_object.aml_required = false;
                current_object.aml_picker_enabled = false;
            });
        },
        watch: {
            current_id: function () {
                let current_object = this;

                current_object.reset_cache_data();

                current_object.message_option.message = '';
                current_object.message_option.message_type = '';
            }
        }
    }
</script>

<style scoped>
/* AML × ordering integration: subtle greyout for identity-verified
   fields locked because the party was sourced from an AmlClient.
   Mirrors the rule in UpdateTrustParty.vue. */
.form_group_register_company_party input[readonly],
.form_group_register_company_party select:disabled,
.form_group_register_company_party .select_container:has(select:disabled) {
    background-color: #f5f5f5 !important;
    color: #6a6a6a !important;
    cursor: not-allowed !important;
}

/* AML-verified pill — mirrors the rule in UpdateTrustParty.vue. */
.form_group_register_company_party .aml_party_pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    margin-top: 0.5em;
    padding: 0.25em 0.7em;
    border-radius: 999px;
    font-size: 0.82em;
    font-weight: 600;
    line-height: 1.4;
}
.form_group_register_company_party .aml_party_pill_clear { background: #dcfce7; color: #166534; }
.form_group_register_company_party .aml_party_pill_warn  { background: #fef3c7; color: #92400e; }
.form_group_register_company_party .aml_party_pill_icon  { font-size: 0.95em; }
.form_group_register_company_party .aml_party_pill_date  { font-weight: 500; opacity: 0.85; }

/* Verified-required prompt — see UpdateTrustParty.vue for rationale. */
.form_group_register_company_party .aml_required_hint p {
    margin: 0.4em 0 0;
    padding: 0.5em 0.8em;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 0.5em;
    font-size: 0.82em;
    color: #92400e;
    line-height: 1.5;
}
</style>
